import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import StackKit from "@/shared/components/kit/stack";
import BoxKit from "@/shared/components/kit/box";
import AvatarUploader from "@/shared/components/common/avatar";
import { DataSidebar, SidebarItem } from "./data";
import { useTheme } from "@mui/material";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
/**
 * Sidebar
 *
 * این کامپوننت سایدبار نسخه دسکتاپ را پیاده‌سازی می‌کند و شامل منوهای اصلی،
 * زیرمنوها، هایلایت مسیر فعال، و قابلیت باز و بسته شدن خودکار با حرکت ماوس است.
 * ساختار داده‌ای منو از DataSidebar خوانده می‌شود و مسیریابی با React Router انجام می‌گیرد.
 *
 * ویژگی‌ها:
 *
 * 1. **باز و بسته شدن سایدبار با حرکت ماوس**
 *    - در حالت عادی عرض سایدبار کوچک است.
 *    - با قرار گرفتن ماوس روی ناحیه سایدبار، بخش متنی منوها باز می‌شود.
 *
 * 2. **مدیریت وضعیت آیتم فعال**
 *    - بررسی مسیر فعلی و مشخص‌کردن آیتم فعال.
 *    - باز نگه‌داشتن زیرمنوی مرتبط با مسیر جاری.
 *
 * 3. **هدایت کاربر بین صفحات**
 *    - کلیک روی آیتم‌هایی که زیرمنو ندارند: رفتن مستقیم به مسیر.
 *    - کلیک روی آیتم‌های دارای زیرمنو: باز و بسته‌شدن منوی والد و هدایت به اولین زیرمنوی موجود.
 *
 * 4. **نمایش زیرمنوها**
 *    - فقط زمانی نشان داده می‌شوند که سایدبار باز باشد و منوی والد نیز فعال باشد.
 *
 * 5. **خروج از حساب کاربری**
 *    - حذف اطلاعات ذخیره‌شده کاربر از localStorage.
 *    - هدایت به صفحه ورود.
 *
 * 6. **نمایش آواتار کاربر**
 *    - با استفاده از AvatarUploader، بسته به باز/بسته بودن سایدبار حالت آواتار تغییر می‌کند.
 *
 * ساختار کلی:
 * @returns JSX.Element
 */

const Sidebar = () => {
  const [isOpenSlider, setIsOpenSlider] = useState(false);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [openParentId, setOpenParentId] = useState<string | null>(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (item: SidebarItem) => {
    if (item.children && item.children.length > 0) {
      setOpenParentId((prev) => (prev === item.id ? null : item.id));

      const firstChild = item.children[0];
      if (firstChild) {
        setActiveItemId(firstChild.id);
        // navigate(firstChild.path);
      }
    } else {
      setActiveItemId(item.id);
      navigate(item.path);
    }
  };

  useEffect(() => {
    DataSidebar.forEach((item) => {
      if (item.path === location.pathname) {
        setActiveItemId(item.id);
        setOpenParentId(null);
      }
      item.children?.forEach((child) => {
        if (child.path === location.pathname) {
          setActiveItemId(child.id);
          setOpenParentId(item.id);
        }
      });
    });
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("userPhone");
    localStorage.removeItem("ID");
    localStorage.removeItem("newrole");
    localStorage.removeItem("roleEnter");
    localStorage.removeItem("token");
    localStorage.removeItem("tokenbest");
    localStorage.removeItem("refreshToken");
    navigate("/login");
  };

  return (
    <>
      <BoxKit sx={{ width: "110px" }}></BoxKit>
      <StackKit direction="column" sx={{position: "fixed",top: 0,right: 0,bgcolor: "#0B0B0B",width: isOpenSlider ? "265px" : "95px",height: "100svh",borderLeft: "2px solid",borderImageSource: "linear-gradient(180deg, rgba(0, 0, 0, 0) 5.37%, #2F2F2F 22.61%, rgba(0, 0, 0, 0) 53.61%)",alignItems: "center",padding: "10px",transition: "width 0.3s ease-in-out",overflow: "hidden",color: "#F3F3F3",zIndex: 100,pl: "5px",}}
        onMouseEnter={() => setIsOpenSlider(true)}
        onMouseLeave={() => setIsOpenSlider(false)}
      >
        {/* Avatar */}
        <BoxKit sx={{width: "100%",display: "flex",justifyContent: "center",mb: 2,}}>
          <AvatarUploader isExpanded={isOpenSlider} />
        </BoxKit>

        <StackKit direction="column"
          sx={{width: "100%",overflowY: "auto",maxHeight: "calc(100svh - 120px)",pr: 1,scrollbarWidth: "thin","&::-webkit-scrollbar": {width: "6px",},"&::-webkit-scrollbar-thumb": {backgroundColor: "#555",borderRadius: "4px",},}}>
          {DataSidebar.map((item) => {
            const isParentActive = activeItemId === item.id || openParentId === item.id;

            return (
              <React.Fragment key={item.id}>
                <BoxKit onClick={() => handleClick(item)} sx={{padding: "10px",fontWeight: "bold",backgroundColor: isParentActive ? theme.palette.primary.main : "transparent",color: isParentActive ? "#fff" : "#ccc",display: "flex",alignItems: "center",borderRadius: "8px",justifyContent: isOpenSlider ? "flex-start" : "center",gap: 1,cursor: "pointer",transition: "background-color 0.3s ease",}}>
                  <span>{item.icon}</span>
                  {isOpenSlider ?
                    <BoxKit component="span" sx={{ maxWidth: isOpenSlider ? "150px" : "0px", whiteSpace: "nowrap", overflow: "hidden", transition: "max-width 0.3s ease", display: "flex", justifyContent: "center", alignItems: "center", }}>
                      <TypoGraphyKit variant="body1" sx={{ fontSize: "14px", fontWeight: "500", fontFamily: "vaziriFontMedium", }}>
                        {item.title}
                      </TypoGraphyKit>
                    </BoxKit>
                    : null}
                </BoxKit>

                {item.children && openParentId === item.id && isOpenSlider && (
                  <StackKit direction="column" sx={{ pl: 3 }}>
                    {item.children.map((child) => (
                      <BoxKit onClick={() => handleClick(child)} key={child.id} sx={{ padding: "6px 10px", fontWeight: "normal", backgroundColor: "transparent", borderRadius: "6px", cursor: "pointer", transition: "background-color 0.3s ease", ml: isOpenSlider ? 1 : 0, textAlign: "right", }}>
                        <TypoGraphyKit variant="body1" sx={{ fontSize: "12px", fontWeight: "400", color: activeItemId === child.id ? theme.palette.primary.main : "#aaa", fontFamily: "vaziriFontReqular", }}>
                          {child.title}
                        </TypoGraphyKit>
                      </BoxKit>
                    ))}
                  </StackKit>
                )}
              </React.Fragment>
            );
          })}
          <BoxKit mt={4} onClick={handleLogout} sx={{ cursor: "pointer", color: "red", display: "flex", alignItems: "center", gap: 1, }}>
            <LoginOutlinedIcon />
            <span>خروج</span>
          </BoxKit>
        </StackKit>
      </StackKit>
    </>
  );
};

export default Sidebar;
